import { useEffect, useState } from "react";
import {
  RevAvatar,
  RevButton,
  RevSpinner,
  RevCheckbox,
  RevEmptyState,
} from "@revmatics/catalyst";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/pro-light-svg-icons";
import { faTrash, faUserPlus } from "@fortawesome/pro-solid-svg-icons";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { useAppDispatch, useAppSelector } from "../../../../stores/hooks/hooks";
import {
  fetchPersonaGroupPersonas,
  removePersonasFromPersonaGroup,
  fetchPersonaGroups,
} from "../../../../stores/thunks/personaGroupsThunk";
import { clearGenerationPersonas } from "../../../../stores/slice/personaGroupsSlice";
import { personaGroupType } from "../../../../stores/types/personaGroupsTypes";
import { PersonaAPIResponseData } from "../../../../stores/types/personasTypes";
import { PersonaDetailView } from "../../../../components/layout/components/globalSlideouts/components/persona/PersonaDetailView.tsx";

type Props = {
  generation: personaGroupType;
  onClose: () => void;
  onTriggerAdd: () => void;
};

export const PersonaGroupDetailView: React.FC<Props> = ({
  generation,
  onTriggerAdd,
}) => {
  const dispatch = useAppDispatch();
  const personas = useAppSelector(
    (state) => state.personaGroups.selectedGenerationPersonas,
  ) as PersonaAPIResponseData[];

  const isLoadingPersonas = useAppSelector(
    (state) => state.personaGroups.isLoadingPersonas,
  );
  const isAssigning = useAppSelector(
    (state) => state.personaGroups.isAssigning,
  );

  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  useEffect(() => {
    if (generation) dispatch(fetchPersonaGroupPersonas(generation.id));
    return () => {
      dispatch(clearGenerationPersonas());
    };
  }, [dispatch, generation]);

  const handleToggleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handleBulkRemove = async () => {
    if (selectedIds.length === 0) return;
    try {
      await dispatch(
        removePersonasFromPersonaGroup({
          id: generation.id,
          body: { persona_ids: selectedIds },
        }),
      ).unwrap();
      setSelectedIds([]);
      await dispatch(fetchPersonaGroupPersonas(generation.id));
      dispatch(fetchPersonaGroups());
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="flex flex-col h-full bg-primary-25">
      <div className="px-6 py-4">
        <div className="flex justify-between items-start">
          <div>
            <h6 className="typography-body-bold-sm text-monochrome-900">
              Personas
            </h6>
            {personas?.length ? (
              <p className="typography-body-xs text-monochrome-500">
                {personas?.length} personas linked
              </p>
            ) : null}
          </div>
          <div className="flex items-center gap-3">
            {selectedIds.length > 0 && (
              <div className="flex items-center gap-3 animate-in fade-in slide-in-from-right-2">
                <span className="typography-body-xs text-monochrome-600">
                  {selectedIds.length}{" "}
                  {selectedIds.length === 1 ? "persona" : "personas"} selected
                </span>
                <RevButton
                  variant="secondary"
                  buttonColor="error"
                  buttonSize="extraSmall"
                  iconLeft={faTrash}
                  text="Delete"
                  handleClick={handleBulkRemove}
                  isLoading={isAssigning}
                />
              </div>
            )}
            <RevButton
              variant="secondary"
              buttonSize="extraSmall"
              iconLeft={faUserPlus}
              text="Add Personas"
              handleClick={onTriggerAdd}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 p-6 pt-2 overflow-y-auto">
        {isLoadingPersonas ? (
          <div className="flex justify-center items-center py-12">
            <RevSpinner size="medium" color="primary" />
          </div>
        ) : (
          <div className="space-y-3">
            {personas && personas.length > 0 ? (
              personas.map((persona) => {
                const isSelected = selectedIds.includes(persona.id);
                return (
                  <Disclosure key={persona.id}>
                    {({ open }) => (
                      <div
                        className={`rounded-md shadow-sm transition-all duration-200 ${isSelected ? "outline-2 outline-primary-500 bg-white" : "outline outline-monochrome-200 bg-white hover:outline-primary-500"}`}
                      >
                        <div className="flex items-center p-2 gap-3">
                          <RevCheckbox
                            checked={isSelected}
                            handleChecked={() => handleToggleSelect(persona.id)}
                          />
                          <DisclosureButton className="flex flex-1 items-center justify-between text-left focus:outline-none">
                            <div className="flex items-center gap-3">
                              <RevAvatar
                                variant="circle"
                                size="medium"
                                theme="light"
                                firstName={persona.name.split(" ")[0]}
                                gender={persona.gender}
                              />
                              <p className="font-medium text-monochrome-900">
                                {persona.name}
                              </p>
                            </div>
                            <FontAwesomeIcon
                              icon={faChevronRight}
                              className={`transition-transform duration-200 text-monochrome-600 ${
                                open ? "rotate-90" : ""
                              }`}
                            />
                          </DisclosureButton>
                        </div>
                        <DisclosurePanel className="px-12 pb-6 border-t border-monochrome-50 pt-4 bg-monochrome-25 rounded-b-md">
                          <PersonaDetailView persona={persona} />
                        </DisclosurePanel>
                      </div>
                    )}
                  </Disclosure>
                );
              })
            ) : (
              <RevEmptyState
                title="No personas yet"
                text="Add a persona to get started"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
